import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useMetric, useMetricValues, useAddMetricValue, useUpdateMetricValue, useDeleteMetricValue } from '@/hooks/use-metrics'
import { useGoals } from '@/hooks/use-goals'
import { useAuth } from '@/hooks/use-auth'
import { MetricChart } from '@/components/metric-chart'
import { GoalStatusBadge } from '@/components/goal-status-badge'
import { Modal } from '@/components/modal'
import { ActionPlansSection } from '@/components/action-plans-section'
import { canCreatePlan } from '../../functions-src/lib/action-plan-access'
import { transformChartData } from '@/lib/metric-chart-data'
import { OPERATOR_SYMBOLS, TIMEFRAME_LABELS, type GoalOperator, type Timeframe } from '@/types'
import { calendarDateOf, noonOf, parseCalendarDate, todayInBusinessZone, formatCalendarDate } from '../../functions-src/lib/calendar'

// Format period display (handles recurring goals with null period_end)
function formatPeriod(start: number, end: number | null | undefined): string {
  const first = formatCalendarDate(calendarDateOf(start))
  if (!end || end === 0) {
    return `Started ${first}`
  }
  return `${first} – ${formatCalendarDate(calendarDateOf(end))}`
}

// Calculate goal status based on latest value (not API-computed current_value)
function calculateGoalStatus(
  latestValue: number | null | undefined,
  operator: GoalOperator,
  targetValue: number,
  targetMin?: number,
  targetMax?: number
): 'met' | 'missed' | 'pending' {
  if (latestValue === undefined || latestValue === null) return 'pending'

  switch (operator) {
    case 'gte':
      return latestValue >= targetValue ? 'met' : 'missed'
    case 'lte':
      return latestValue <= targetValue ? 'met' : 'missed'
    case 'range':
      return latestValue >= (targetMin ?? 0) && latestValue <= (targetMax ?? Infinity) ? 'met' : 'missed'
    default:
      return 'pending'
  }
}

export function MetricDetail() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const { data: metric, isLoading: metricLoading } = useMetric(id)
  const { data: goals, isLoading: goalsLoading } = useGoals({ metric_id: id })
  const { data: values } = useMetricValues(id)
  const addValue = useAddMetricValue()
  const updateValue = useUpdateMetricValue()
  const deleteValue = useDeleteMetricValue()

  const [isValueModalOpen, setIsValueModalOpen] = useState(false)
  const [editingValueId, setEditingValueId] = useState<string | null>(null)
  const [valueForm, setValueForm] = useState({ value: '', recorded_at: '' })
  const [originalDate, setOriginalDate] = useState('')

  const isAdmin = user?.role === 'admin'
  const isLoading = metricLoading || goalsLoading

  const openAddValue = () => {
    setEditingValueId(null)
    setValueForm({ value: '', recorded_at: todayInBusinessZone() })
    setIsValueModalOpen(true)
  }

  const openEditValue = (v: { id: string; value: number; recorded_at: number }) => {
    setEditingValueId(v.id)
    const date = calendarDateOf(v.recorded_at)
    setOriginalDate(date)
    setValueForm({ value: v.value.toString(), recorded_at: date })
    setIsValueModalOpen(true)
  }

  const handleValueSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id) return
    const date = parseCalendarDate(valueForm.recorded_at)
    if (!date) {
      alert('Enter a valid date.')
      return
    }
    if (editingValueId) {
      // Only send the date when it was changed, so editing just the value keeps the reading's exact time
      const recorded_at = date === originalDate ? undefined : noonOf(date)
      await updateValue.mutateAsync({ metricId: id, valueId: editingValueId, value: parseFloat(valueForm.value), recorded_at })
    } else {
      const recorded_at = noonOf(date)
      await addValue.mutateAsync({ metricId: id, value: parseFloat(valueForm.value), recorded_at })
    }
    setIsValueModalOpen(false)
  }

  const handleDeleteValue = async (valueId: string) => {
    if (!id || !confirm('Delete this value?')) return
    await deleteValue.mutateAsync({ metricId: id, valueId })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal" />
      </div>
    )
  }

  if (!metric) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Metric Not Found</h2>
        <Link to="/" className="text-brand-teal hover:underline">
          Back to Dashboard
        </Link>
      </div>
    )
  }

  // Get all active goals (current period or recurring) for this metric
  const now = Math.floor(Date.now() / 1000)
  const activeGoals = goals?.filter((g) => g.period_start <= now && (!g.period_end || g.period_end >= now)) ?? []
  const activeGoal = activeGoals[0] ?? undefined

  // Chart data - use values from useMetricValues hook, format labels based on goal timeframe
  const chartData = values ? transformChartData(values, activeGoal?.timeframe as Timeframe | undefined) : []

  // Get current period value (second-to-last item, since last is forecast)
  // Find the most recent non-forecast period with actual data
  const currentPeriodValue = chartData.length > 1
    ? chartData.filter(d => !d.isForecast && d.value !== null).slice(-1)[0] ?? null
    : null

  // Calculate goal status based on aggregated period value
  const computedStatus = activeGoal
    ? calculateGoalStatus(
        currentPeriodValue?.value,
        activeGoal.operator as GoalOperator,
        activeGoal.target_value,
        activeGoal.target_min ?? undefined,
        activeGoal.target_max ?? undefined
      )
    : undefined

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm text-gray-500">
        <Link to="/" className="hover:text-brand-teal">
          Scorecard
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-900">{metric.name}</span>
      </nav>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            {metric.name}
            {metric.unit && <span className="text-lg text-gray-400">({metric.unit})</span>}
          </h1>
          {metric.description && (
            <p className="text-gray-600 mt-1">{metric.description}</p>
          )}
          {isAdmin && (
            <p className="text-xs text-gray-400 mt-1 font-mono">ID: {metric.id}</p>
          )}
        </div>
        {computedStatus && <GoalStatusBadge status={computedStatus} />}
      </div>

      {/* Current period value + target */}
      {(currentPeriodValue || activeGoal) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="stat-card">
            <p className="text-sm text-gray-500 mb-1">
              {currentPeriodValue && (currentPeriodValue.count ?? 0) > 1 ? 'Period Average' : 'Current Value'}
            </p>
            <p className="text-3xl font-bold text-gray-900">
              {currentPeriodValue && currentPeriodValue.value !== null
                ? metric.unit === '$'
                  ? `$${currentPeriodValue.value.toLocaleString()}`
                  : metric.unit === '%'
                    ? `${currentPeriodValue.value}%`
                    : currentPeriodValue.value.toLocaleString()
                : '—'}
            </p>
            {currentPeriodValue && currentPeriodValue.value !== null && (
              <p className="text-xs text-gray-400 mt-1">
                {(currentPeriodValue.count ?? 0) > 1
                  ? `${currentPeriodValue.count} readings averaged`
                  : currentPeriodValue.date}
              </p>
            )}
          </div>
          {activeGoal && (
            <>
              <div className="stat-card">
                <p className="text-sm text-gray-500 mb-1">Target</p>
                <p className="text-3xl font-bold text-gray-900">
                  {activeGoal.operator === 'range'
                    ? `${activeGoal.target_min} – ${activeGoal.target_max}`
                    : `${OPERATOR_SYMBOLS[activeGoal.operator as GoalOperator]} ${
                        metric.unit === '$'
                          ? `$${activeGoal.target_value.toLocaleString()}`
                          : metric.unit === '%'
                            ? `${activeGoal.target_value}%`
                            : activeGoal.target_value.toLocaleString()
                      }`}
                </p>
              </div>
              <div className="stat-card">
                <p className="text-sm text-gray-500 mb-1">Period</p>
                <p className="text-lg font-medium text-gray-900">
                  {TIMEFRAME_LABELS[activeGoal.timeframe as Timeframe]}
                </p>
                <p className="text-sm text-gray-500">
                  {formatPeriod(activeGoal.period_start, activeGoal.period_end)}
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Chart */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Historical Trend</h2>
        <MetricChart
          data={chartData}
          unit={metric.unit}
          targetValue={activeGoal?.target_value}
          targetMin={activeGoal?.target_min ?? undefined}
          targetMax={activeGoal?.target_max ?? undefined}
          operator={activeGoal?.operator as GoalOperator | undefined}
        />
      </div>

      {/* All goals for this metric */}
      {goals && goals.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Goal History</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap min-w-[160px]">Period</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap min-w-[100px]">Timeframe</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase whitespace-nowrap min-w-[100px]">Target</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase whitespace-nowrap min-w-[80px]">Actual</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase whitespace-nowrap min-w-[80px]">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {goals.map((goal) => {
                  // For active/ongoing goals, use aggregated period value
                  const isActiveGoal = goal.period_start <= now && (!goal.period_end || goal.period_end >= now)
                  const displayValue = isActiveGoal ? currentPeriodValue?.value : goal.current_value
                  const displayStatus = isActiveGoal
                    ? calculateGoalStatus(
                        currentPeriodValue?.value,
                        goal.operator as GoalOperator,
                        goal.target_value,
                        goal.target_min ?? undefined,
                        goal.target_max ?? undefined
                      )
                    : goal.status

                  return (
                    <tr key={goal.id} className="hover:bg-gray-50">
                      <td className="sticky left-0 z-10 bg-white px-4 py-4 text-sm text-gray-600 whitespace-nowrap border-r border-gray-100">
                        {formatPeriod(goal.period_start, goal.period_end)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">
                        {TIMEFRAME_LABELS[goal.timeframe as Timeframe]}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900 text-right font-medium whitespace-nowrap">
                        {goal.operator === 'range'
                          ? `${goal.target_min} – ${goal.target_max}`
                          : `${OPERATOR_SYMBOLS[goal.operator as GoalOperator]} ${goal.target_value}`}
                      </td>
                      <td className="px-4 py-4 text-sm text-right font-medium whitespace-nowrap">
                        <span className={displayStatus === 'met' ? 'text-success-700' : displayStatus === 'missed' ? 'text-danger-700' : 'text-gray-600'}>
                          {displayValue !== null && displayValue !== undefined
                            ? displayValue.toLocaleString()
                            : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <GoalStatusBadge status={displayStatus} size="sm" />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Action Plans Sections (one per active goal on this metric) */}
      {activeGoals.map((goal) => (
        <div key={goal.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <ActionPlansSection
            goalId={goal.id}
            role={user?.role}
            canCreate={canCreatePlan(user?.role, user?.id, goal.owner_id, goal.period_end, now)}
            goalOwnerId={goal.owner_id}
            goalOwnerName={activeGoals.length > 1 ? (goal.owner_name ?? 'Unknown') : undefined}
            currentUserId={user?.id}
          />
        </div>
      ))}

      {/* Admin: Values Management */}
      {isAdmin && values && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Metric Values</h2>
            <button onClick={openAddValue} className="btn btn-primary text-sm">
              + Add Value
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap min-w-[120px]">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase whitespace-nowrap min-w-[80px]">Value</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap min-w-[80px]">Source</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase whitespace-nowrap min-w-[100px]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {values.map((v) => (
                  <tr key={v.id} className="hover:bg-gray-50">
                    <td className="sticky left-0 z-10 bg-white px-4 py-4 text-sm text-gray-600 whitespace-nowrap border-r border-gray-100">{formatCalendarDate(calendarDateOf(v.recorded_at))}</td>
                    <td className="px-4 py-4 text-sm text-gray-900 text-right font-medium whitespace-nowrap">{v.value}</td>
                    <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">{v.source || 'manual'}</td>
                    <td className="px-4 py-4 text-right space-x-2 whitespace-nowrap">
                      <button onClick={() => openEditValue(v)} className="text-brand-teal hover:text-brand-teal/80 text-sm">
                        Edit
                      </button>
                      <button onClick={() => handleDeleteValue(v.id)} className="text-danger-500 hover:text-danger-200 text-sm">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {values.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                      No values recorded yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Value Modal */}
      <Modal isOpen={isValueModalOpen} onClose={() => setIsValueModalOpen(false)} title={editingValueId ? 'Edit Value' : 'Add Value'}>
        <form onSubmit={handleValueSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Value *</label>
            <input
              type="number"
              step="any"
              required
              value={valueForm.value}
              onChange={(e) => setValueForm({ ...valueForm, value: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
              placeholder="Enter value"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
            <input
              type="date"
              required
              value={valueForm.recorded_at}
              onChange={(e) => setValueForm({ ...valueForm, recorded_at: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setIsValueModalOpen(false)} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={addValue.isPending || updateValue.isPending}>
              {editingValueId ? 'Save' : 'Add'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Admin edit link */}
      {isAdmin && (
        <div className="text-center">
          <Link to="/admin/metrics" className="text-sm text-brand-teal hover:underline">
            Edit Metric Settings
          </Link>
        </div>
      )}
    </div>
  )
}
